import { Link, Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Gift, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { resolvePostLoginPath } from "@/lib/auth-redirect";

const benefits = [
  {
    icon: Gift,
    title: "Refer & earn",
    description: "Multi-level commissions on every purchase in your network.",
  },
  {
    icon: Sparkles,
    title: "B.V rewards",
    description: "Cashback, coupons and reward legs after Member ID activation.",
  },
  {
    icon: MapPin,
    title: "Local delivery",
    description: "Shop from nearby stores with transparent pricing and fast delivery.",
  },
];

const pageMeta: Record<string, { title: string; subtitle: string }> = {
  "/auth/login": {
    title: "Welcome back",
    subtitle: "Sign in to manage your orders, store or account.",
  },
  "/auth/admin/secure/login": {
    title: "Admin sign in",
    subtitle: "Secure access to the Bihari Mall control panel.",
  },
  "/auth/register": {
    title: "Create your account",
    subtitle: "Join Bihari Mall in under a minute — no credit card required.",
  },
  "/auth/register-store": {
    title: "Register Your Store",
    subtitle: "Expand your shop digitally and connect with neighboring shoppers.",
  },
  "/auth/register-delivery": {
    title: "Become a Delivery Partner",
    subtitle: "Apply once — deliver nearby orders and earn weekly payouts.",
  },
  "/auth/forgot-password": {
    title: "Reset your password",
    subtitle: "We'll email you a secure link to set a new password.",
  },
  "/auth/reset-password": {
    title: "Set a new password",
    subtitle: "Choose something strong and easy to remember.",
  },
  "/auth/otp": {
    title: "Sign in with phone",
    subtitle: "We'll send a one-time code to your mobile number.",
  },
};

const AuthLayout = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const meta = pageMeta[location.pathname] ?? {
    title: "Welcome",
    subtitle: "Bihari Mall online marketplace.",
  };

  const returnTo =
    (location.state as { from?: string } | null)?.from ||
    searchParams.get("returnUrl") ||
    searchParams.get("redirectTo");

  const stayOnAuthForm =
    location.pathname === "/auth/register-store" ||
    location.pathname === "/auth/register-delivery";

  if (!isLoading && isAuthenticated && user && !stayOnAuthForm) {
    return <Navigate to={resolvePostLoginPath(user.role, returnTo)} replace />;
  }

  return (
    <div className="min-h-screen bg-muted/40 dark:bg-black flex flex-col lg:flex-row">
      {/* Brand / story panel — desktop only */}
      <aside className="relative hidden lg:flex lg:w-[46%] xl:w-[44%] mesh-gradient text-primary-foreground flex-col overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-white/5 blur-[140px]" />
        </div>

        <div className="relative z-10 flex h-full flex-col px-10 py-12 xl:px-14">
          <Link
            to="/"
            className="inline-flex w-fit items-center gap-2 text-sm font-medium text-primary-foreground/70 transition-colors hover:text-primary-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to store
          </Link>

          <div className="my-auto max-w-md space-y-10">
            <div>
              <Link to="/" className="inline-flex items-center gap-3">
                <img src="/icon.png" alt="Bihari Mall Icon" className="h-12 w-12 object-contain" />
                <div>
                  <img src="/nav-logo.png" alt="Bihari Mall" className="h-8 w-auto object-contain brightness-0 invert" />
                </div>
              </Link>

              <h1 className="mt-10 font-heading text-4xl font-bold leading-[1.15] tracking-tight text-white">
                Shop local.
                <br />
                <span className="text-white/75">Earn together.</span>
              </h1>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-primary-foreground/75">
                Join India&apos;s local network — shop from nearby stores, refer friends,
                and unlock multi-level rewards on every purchase.
              </p>
            </div>

            <div className="space-y-3">
              {benefits.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <item.icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-primary-foreground/65">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-2 text-xs text-primary-foreground/55">
            <ShieldCheck className="h-3.5 w-3.5" />
            Bank-grade encryption · ISO 27001
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex flex-1 flex-col bg-background dark:bg-black">
        {/* Compact header — mobile only */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 lg:hidden">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/icon.png" alt="Bihari Mall Icon" className="h-8 w-8 object-contain" />
            <img src="/nav-logo.png" alt="Bihari Mall" className="h-6 w-auto object-contain dark:invert" />
          </Link>
          <Link
            to="/"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Back
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8 lg:px-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: "easeOut" }}
            className={`w-full ${
              location.pathname === "/auth/register-store" ||
              location.pathname === "/auth/register-delivery"
                ? "max-w-[640px]"
                : "max-w-[440px]"
            }`}
          >
            <div className="mb-7">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-[28px]">
                {meta.title}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">{meta.subtitle}</p>
            </div>

            <Outlet />
          </motion.div>
        </div>

        <footer className="flex flex-col items-center justify-between gap-3 border-t border-border px-6 py-5 text-xs text-muted-foreground sm:flex-row lg:px-10">
          <div className="flex items-center gap-5">
            <Link to="/terms" className="transition-colors hover:text-foreground">
              Terms
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-foreground">
              Privacy
            </Link>
            <Link to="/help" className="transition-colors hover:text-foreground">
              Help
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Bihari Mall</p>
        </footer>
      </main>
    </div>
  );
};

export default AuthLayout;
